import * as THREE from 'three';

/**
 * Weather system with 4 states: CLEAR, RAIN, THUNDER, SNOW.
 *
 * - Rain/Thunder: blue-white particles falling fast around the player.
 * - Snow: white particles drifting slowly (only in Tundra biome).
 * - Thunder adds lightning flashes and screen shake.
 * - Weather transitions every 5-10 minutes with smooth ramp up/down.
 */

// Weather states
const WeatherState = {
  CLEAR:   0,
  RAIN:    1,
  THUNDER: 2,
  SNOW:    3,
};

// Biome IDs (mirrors TerrainGenerator's local BIOME enum)
const BIOME = { PLAINS: 0, DESERT: 1, FOREST: 2, TUNDRA: 3, MOUNTAINS: 4 };

// Particle counts and radii
const RAIN_COUNT     = 3000;
const RAIN_RADIUS    = 50;
const RAIN_SPEED     = 20;
const RAIN_HEIGHT    = 40;  // spawn height above player

const SNOW_COUNT     = 2000;
const SNOW_RADIUS    = 40;
const SNOW_SPEED     = 5;
const SNOW_HEIGHT    = 30;

// Transition
const TRANSITION_DURATION = 10; // seconds to ramp particles up/down

// Lightning
const LIGHTNING_MIN_INTERVAL = 5;
const LIGHTNING_MAX_INTERVAL = 15;
const LIGHTNING_FLASH_DURATION = 0.2;

// Weather change timing
const WEATHER_MIN_DURATION = 300; // 5 minutes
const WEATHER_MAX_DURATION = 600; // 10 minutes

// Weather probability weights
const WEATHER_WEIGHTS = {
  [WeatherState.CLEAR]:   0.60,
  [WeatherState.RAIN]:    0.25,
  [WeatherState.THUNDER]: 0.10,
  [WeatherState.SNOW]:    0.05,
};

export class Weather {
  /**
   * @param {THREE.Scene} scene
   * @param {import('../world/DayNight.js').DayNight} dayNight
   */
  constructor(scene, dayNight) {
    this.scene = scene;
    this.dayNight = dayNight;
    this._soundManager = null;

    // Current and target weather
    this._state = WeatherState.CLEAR;
    this._targetState = WeatherState.CLEAR;
    this._intensity = 0;          // 0 = nothing, 1 = full
    this._targetIntensity = 0;

    // Timer until next weather change
    this._weatherTimer = this._randomDuration();

    // ---- Rain particle system ----
    this._rainGeo = new THREE.BufferGeometry();
    const rainPositions = new Float32Array(RAIN_COUNT * 3);
    const rainVelocities = new Float32Array(RAIN_COUNT); // y speeds (for slight variation)
    for (let i = 0; i < RAIN_COUNT; i++) {
      rainPositions[i * 3]     = 0;
      rainPositions[i * 3 + 1] = 0;
      rainPositions[i * 3 + 2] = 0;
      rainVelocities[i] = RAIN_SPEED + (Math.random() - 0.5) * 4; // 18-22
    }
    this._rainGeo.setAttribute('position', new THREE.BufferAttribute(rainPositions, 3));
    this._rainVelocities = rainVelocities;

    const rainMat = new THREE.PointsMaterial({
      color: 0xAABBDD,
      size: 0.15,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: false,
      sizeAttenuation: true,
    });
    this._rainPoints = new THREE.Points(this._rainGeo, rainMat);
    this._rainPoints.frustumCulled = false;
    this._rainPoints.visible = false;
    scene.add(this._rainPoints);

    // Distribute rain particles in a cylinder around origin
    this._initParticles(this._rainGeo, RAIN_COUNT, RAIN_RADIUS, RAIN_HEIGHT);

    // ---- Snow particle system ----
    this._snowGeo = new THREE.BufferGeometry();
    const snowPositions = new Float32Array(SNOW_COUNT * 3);
    const snowPhases = new Float32Array(SNOW_COUNT); // for sin wave drift
    for (let i = 0; i < SNOW_COUNT; i++) {
      snowPositions[i * 3]     = 0;
      snowPositions[i * 3 + 1] = 0;
      snowPositions[i * 3 + 2] = 0;
      snowPhases[i] = Math.random() * Math.PI * 2;
    }
    this._snowGeo.setAttribute('position', new THREE.BufferAttribute(snowPositions, 3));
    this._snowPhases = snowPhases;

    const snowMat = new THREE.PointsMaterial({
      color: 0xFFFFFF,
      size: 0.2,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: false,
      sizeAttenuation: true,
    });
    this._snowPoints = new THREE.Points(this._snowGeo, snowMat);
    this._snowPoints.frustumCulled = false;
    this._snowPoints.visible = false;
    scene.add(this._snowPoints);

    this._initParticles(this._snowGeo, SNOW_COUNT, SNOW_RADIUS, SNOW_HEIGHT);

    // ---- Lightning flash overlay ----
    this._lightningOverlay = this._createLightningOverlay();
    this._lightningTimer = this._randomLightningInterval();
    this._lightningFlashTime = 0;
    this._lightningActive = false;

    // ---- Screen shake state ----
    this._shakeOffset = new THREE.Vector3();
    this._shakeTimer = 0;

    // ---- Elapsed time (for snow drift) ----
    this._elapsed = 0;

    // ---- Sky darkening ----
    this._baseFogFar = scene.fog ? scene.fog.far : 100;

    // Store the player position for centering particles
    this._playerPos = new THREE.Vector3();
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------

  /**
   * @param {number} dt - delta time in seconds
   * @param {THREE.Vector3} playerPos
   * @param {number} biome - biome ID at player position (from TerrainGenerator.getBiome)
   */
  update(dt, playerPos, biome) {
    this._elapsed += dt;
    this._playerPos.copy(playerPos);

    // --- Weather change timer ---
    this._weatherTimer -= dt;
    if (this._weatherTimer <= 0) {
      this._pickNewWeather(biome);
      this._weatherTimer = this._randomDuration();
    }

    // --- Override snow: only in cold biomes ---
    if (this._targetState === WeatherState.SNOW && biome !== BIOME.TUNDRA) {
      // If player leaves tundra, switch snow to rain instead
      this._targetState = WeatherState.RAIN;
      this._targetIntensity = 1;
    }

    // --- Smooth intensity transition ---
    if (this._state !== this._targetState) {
      // Ramp down current weather first
      this._intensity -= dt / TRANSITION_DURATION;
      if (this._intensity <= 0) {
        this._intensity = 0;
        this._state = this._targetState;
      }
    } else {
      // Ramp up toward target intensity
      const diff = this._targetIntensity - this._intensity;
      if (Math.abs(diff) > 0.001) {
        this._intensity += Math.sign(diff) * dt / TRANSITION_DURATION;
        this._intensity = THREE.MathUtils.clamp(this._intensity, 0, 1);
      }
    }

    // --- Update particle systems ---
    const isRainActive = this._state === WeatherState.RAIN || this._state === WeatherState.THUNDER;
    const isSnowActive = this._state === WeatherState.SNOW;

    this._updateRain(dt, isRainActive);
    this._updateSnow(dt, isSnowActive);

    // --- Thunder-specific: lightning flashes ---
    if (this._state === WeatherState.THUNDER && this._intensity > 0.5) {
      this._updateLightning(dt);
    } else {
      this._lightningOverlay.style.opacity = '0';
      this._lightningActive = false;
    }

    // --- Screen shake ---
    this._updateShake(dt);

    // --- Sky darkening during rain/thunder ---
    this._updateSkyDarkening();
  }

  /** @returns {boolean} True if currently raining (RAIN or THUNDER state with active intensity). */
  isRaining() {
    return (this._state === WeatherState.RAIN || this._state === WeatherState.THUNDER) && this._intensity > 0.1;
  }

  /** @returns {boolean} True if currently thundering. */
  isThundering() {
    return this._state === WeatherState.THUNDER && this._intensity > 0.1;
  }

  /** @returns {boolean} True if currently snowing. */
  isSnowing() {
    return this._state === WeatherState.SNOW && this._intensity > 0.1;
  }

  /** Get the current camera shake offset (add to camera position). */
  getShakeOffset() {
    return this._shakeOffset;
  }

  setSoundManager(sm) { this._soundManager = sm; }

  /** Force a specific weather state via terminal command. */
  forceWeather(type) {
    const map = { 'clear': WeatherState.CLEAR, 'rain': WeatherState.RAIN, 'thunder': WeatherState.THUNDER, 'snow': WeatherState.SNOW };
    const state = map[type];
    if (state === undefined) return;
    this._state = state;
    this._targetState = state;
    this._intensity = state === WeatherState.CLEAR ? 0 : 1;
    this._targetIntensity = this._intensity;
    this._weatherTimer = this._randomDuration();
  }

  /** Get the current weather state string (for HUD/debug). */
  getStateName() {
    switch (this._state) {
      case WeatherState.CLEAR:   return 'Despejado';
      case WeatherState.RAIN:    return 'Lluvia';
      case WeatherState.THUNDER: return 'Tormenta';
      case WeatherState.SNOW:    return 'Nieve';
      default: return 'Desconocido';
    }
  }

  /** Get intensity (0-1). */
  getIntensity() {
    return this._intensity;
  }

  /** Force a specific weather state (e.g. from terminal command). */
  setWeather(stateName) {
    const map = {
      clear:   WeatherState.CLEAR,
      rain:    WeatherState.RAIN,
      thunder: WeatherState.THUNDER,
      snow:    WeatherState.SNOW,
    };
    const s = map[stateName.toLowerCase()];
    if (s !== undefined) {
      this._targetState = s;
      this._targetIntensity = s === WeatherState.CLEAR ? 0 : 1;
      this._weatherTimer = this._randomDuration();
    }
  }

  // ------------------------------------------------------------------
  // Internal: particle initialization
  // ------------------------------------------------------------------

  _initParticles(geometry, count, radius, height) {
    const positions = geometry.getAttribute('position').array;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius;
      positions[i * 3]     = Math.cos(angle) * dist;
      positions[i * 3 + 1] = Math.random() * height;
      positions[i * 3 + 2] = Math.sin(angle) * dist;
    }
    geometry.getAttribute('position').needsUpdate = true;
  }

  // ------------------------------------------------------------------
  // Internal: rain update
  // ------------------------------------------------------------------

  _updateRain(dt, active) {
    const opacity = active ? this._intensity * 0.6 : this._intensity * 0.6;
    const visible = active && this._intensity > 0.01;

    this._rainPoints.visible = visible;
    this._rainPoints.material.opacity = active ? this._intensity * 0.6 : 0;

    if (!visible) return;

    const positions = this._rainGeo.getAttribute('position').array;
    const px = this._playerPos.x;
    const py = this._playerPos.y;
    const pz = this._playerPos.z;

    for (let i = 0; i < RAIN_COUNT; i++) {
      const i3 = i * 3;

      // Fall
      positions[i3 + 1] -= this._rainVelocities[i] * dt;

      // Respawn if below ground level (relative to player)
      if (positions[i3 + 1] < -10) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.random() * RAIN_RADIUS;
        positions[i3]     = Math.cos(angle) * dist;
        positions[i3 + 1] = RAIN_HEIGHT + Math.random() * 5;
        positions[i3 + 2] = Math.sin(angle) * dist;
      }
    }

    this._rainGeo.getAttribute('position').needsUpdate = true;

    // Center rain around player
    this._rainPoints.position.set(px, py, pz);
  }

  // ------------------------------------------------------------------
  // Internal: snow update
  // ------------------------------------------------------------------

  _updateSnow(dt, active) {
    const visible = active && this._intensity > 0.01;

    this._snowPoints.visible = visible;
    this._snowPoints.material.opacity = active ? this._intensity * 0.8 : 0;

    if (!visible) return;

    const positions = this._snowGeo.getAttribute('position').array;
    const px = this._playerPos.x;
    const py = this._playerPos.y;
    const pz = this._playerPos.z;
    const time = this._elapsed;

    for (let i = 0; i < SNOW_COUNT; i++) {
      const i3 = i * 3;

      // Fall slowly
      positions[i3 + 1] -= SNOW_SPEED * dt;

      // Horizontal drift (sin wave)
      const phase = this._snowPhases[i];
      positions[i3]     += Math.sin(time * 0.8 + phase) * 0.3 * dt;
      positions[i3 + 2] += Math.cos(time * 0.6 + phase * 1.3) * 0.2 * dt;

      // Respawn if below ground level
      if (positions[i3 + 1] < -10) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.random() * SNOW_RADIUS;
        positions[i3]     = Math.cos(angle) * dist;
        positions[i3 + 1] = SNOW_HEIGHT + Math.random() * 5;
        positions[i3 + 2] = Math.sin(angle) * dist;
      }
    }

    this._snowGeo.getAttribute('position').needsUpdate = true;

    // Center snow around player
    this._snowPoints.position.set(px, py, pz);
  }

  // ------------------------------------------------------------------
  // Internal: lightning
  // ------------------------------------------------------------------

  _createLightningOverlay() {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      background: white;
      opacity: 0;
      pointer-events: none;
      z-index: 1000;
      transition: opacity 0.05s;
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  _updateLightning(dt) {
    this._lightningTimer -= dt;

    // Active flash
    if (this._lightningActive) {
      this._lightningFlashTime += dt;
      const t = this._lightningFlashTime / LIGHTNING_FLASH_DURATION;
      if (t >= 1) {
        // Flash ended
        this._lightningActive = false;
        this._lightningOverlay.style.opacity = '0';
        this._lightningTimer = this._randomLightningInterval();
      } else {
        // Triangle wave: up then down
        const brightness = t < 0.3 ? (t / 0.3) * 0.8 : 0.8 * (1 - (t - 0.3) / 0.7);
        this._lightningOverlay.style.opacity = String(Math.max(0, brightness));
      }
      return;
    }

    // Trigger new flash
    if (this._lightningTimer <= 0) {
      this._lightningActive = true;
      this._lightningFlashTime = 0;
      this._shakeTimer = 0.1; // Trigger screen shake

      if (this._soundManager) this._soundManager.playThunder();
    }
  }

  _randomLightningInterval() {
    return LIGHTNING_MIN_INTERVAL + Math.random() * (LIGHTNING_MAX_INTERVAL - LIGHTNING_MIN_INTERVAL);
  }

  // ------------------------------------------------------------------
  // Internal: screen shake
  // ------------------------------------------------------------------

  _updateShake(dt) {
    if (this._shakeTimer > 0) {
      this._shakeTimer -= dt;
      const mag = 0.05 * (this._shakeTimer / 0.1); // diminishing
      this._shakeOffset.set(
        (Math.random() - 0.5) * mag,
        (Math.random() - 0.5) * mag,
        (Math.random() - 0.5) * mag
      );
    } else {
      this._shakeOffset.set(0, 0, 0);
    }
  }

  // ------------------------------------------------------------------
  // Internal: sky darkening
  // ------------------------------------------------------------------

  _updateSkyDarkening() {
    if (!this.dayNight) return;

    const isRainOrThunder = this._state === WeatherState.RAIN || this._state === WeatherState.THUNDER;
    if (isRainOrThunder && this._intensity > 0.01) {
      // Darken ambient light by up to 30%
      const darkenFactor = 1.0 - 0.3 * this._intensity;
      this.dayNight.ambientLight.intensity *= darkenFactor;
      this.dayNight.directionalLight.intensity *= darkenFactor;

      // Pull in fog
      if (this.scene.fog) {
        this.scene.fog.far *= (1.0 - 0.25 * this._intensity);
      }
    }
  }

  // ------------------------------------------------------------------
  // Internal: weather selection
  // ------------------------------------------------------------------

  _pickNewWeather(biome) {
    const r = Math.random();
    let cumulative = 0;

    for (const [state, weight] of Object.entries(WEATHER_WEIGHTS)) {
      cumulative += weight;
      if (r <= cumulative) {
        const s = Number(state);
        // Snow only in tundra
        if (s === WeatherState.SNOW && biome !== BIOME.TUNDRA) {
          this._targetState = WeatherState.CLEAR;
          this._targetIntensity = 0;
          return;
        }
        this._targetState = s;
        this._targetIntensity = s === WeatherState.CLEAR ? 0 : 1;
        return;
      }
    }
    // Fallback
    this._targetState = WeatherState.CLEAR;
    this._targetIntensity = 0;
  }

  _randomDuration() {
    return WEATHER_MIN_DURATION + Math.random() * (WEATHER_MAX_DURATION - WEATHER_MIN_DURATION);
  }

  serialize() {
    return {
      state: this._state,
      targetState: this._targetState,
      intensity: this._intensity,
      targetIntensity: this._targetIntensity,
      weatherTimer: this._weatherTimer,
    };
  }

  deserialize(data) {
    if (!data) return;
    if (data.state !== undefined) this._state = data.state;
    if (data.targetState !== undefined) this._targetState = data.targetState;
    if (data.intensity !== undefined) this._intensity = data.intensity;
    if (data.targetIntensity !== undefined) this._targetIntensity = data.targetIntensity;
    if (data.weatherTimer !== undefined) this._weatherTimer = data.weatherTimer;
  }
}
